/** SUNTRONIX ONE — Force-Override compliance report.
 *
 *  Cross-border logistics gate overrides captured in `audit_logs` under
 *  the `logistics.force_override` action.  Every row is who did it,
 *  which customer, which country pair, whether they gave a reason and
 *  what the lane state was at the time.  Read-only.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { RefreshCcw, ShieldAlert, Download, Globe2 } from "lucide-react";

const WINDOWS = [
  { label: "7 days",   value: 7 },
  { label: "30 days",  value: 30 },
  { label: "90 days",  value: 90 },
  { label: "365 days", value: 365 },
];

export default function ForceOverrides() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (windowDays) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/audit/force-overrides", {
        params: { days: windowDays } });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load overrides");
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const exportCsv = () => {
    if (!data?.items?.length) return;
    const header = ["Timestamp", "Operator", "Role", "Master Company",
                     "Request ID", "Origin", "Destination", "Reason",
                     "Lane Present", "Lane Restricted"];
    const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    const lines = [header.join(",")];
    for (const it of data.items) {
      lines.push([
        it.timestamp, it.operator, it.role, it.master_company_name || it.master_company_id,
        it.request_id, it.origin_country, it.destination_country,
        it.reason, it.lane_present, it.lane_restricted,
      ].map(esc).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `force_overrides_${days}d.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const c = data?.counts || {};
  const items = data?.items || [];

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="force-overrides-page">
      <PageHeader
        eyebrow="Admin · Compliance"
        title="Force-override report"
        context="Every cross-border logistics gate that was bypassed with the X-Force-Cross-Border header — operator, reason, lane state and country pair, together in one place."
        actions={
          <div className="flex items-center gap-2">
            <select
              className="text-[12px] border rounded px-2 py-1"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="force-overrides-window">
              {WINDOWS.map((w) => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
            <Button variant="outline" onClick={() => load(days)}
              data-testid="force-overrides-refresh">
              <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
            <Button variant="outline" onClick={exportCsv}
              disabled={!items.length}
              data-testid="force-overrides-export">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
            </Button>
          </div>
        }
        testId="force-overrides-header"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total overrides", n: c.total ?? 0, testid: "kpi-total",
            icon: ShieldAlert, tint: "text-red-700" },
          { label: "Distinct operators", n: c.distinct_operators ?? 0,
            testid: "kpi-operators", icon: null, tint: "text-slate-900" },
          { label: "Distinct country pairs", n: c.distinct_pairs ?? 0,
            testid: "kpi-pairs", icon: Globe2, tint: "text-slate-900" },
          { label: "Missing reason", n: c.missing_reason ?? 0,
            testid: "kpi-missing-reason", icon: null,
            tint: (c.missing_reason ?? 0) > 0 ? "text-amber-700" : "text-slate-900" },
        ].map((k) => (
          <div key={k.label} className="st-panel p-3"
            data-testid={`force-overrides-${k.testid}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
              {k.icon && <k.icon className="h-3 w-3" />}
              {k.label}
            </div>
            <div className={"text-2xl font-heading font-semibold mt-1 " + k.tint}>
              {k.n}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Top operators" testId="force-overrides-by-operator">
          <div className="st-panel p-3">
            {(data?.by_operator || []).length === 0 && (
              <div className="text-slate-400 text-[12px] text-center py-4">
                No overrides in this window.
              </div>
            )}
            {(data?.by_operator || []).map((r) => (
              <div key={r.operator} className="flex items-center justify-between text-[12px] py-1">
                <span className="truncate">{r.operator}</span>
                <span className="font-mono text-slate-900">{r.count}</span>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Top country pairs" testId="force-overrides-by-pair">
          <div className="st-panel p-3">
            {(data?.by_pair || []).length === 0 && (
              <div className="text-slate-400 text-[12px] text-center py-4">
                No overrides in this window.
              </div>
            )}
            {(data?.by_pair || []).map((r) => (
              <div key={r.pair} className="flex items-center justify-between text-[12px] py-1">
                <span>{r.pair}</span>
                <span className="font-mono text-slate-900">{r.count}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section title={`${items.length} overrides`} testId="force-overrides-list">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Operator</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Route</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">Lane</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-slate-400 text-center py-6">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="text-slate-500 text-center py-6">
                  No force-overrides recorded in the last {days} days.
                </td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100"
                  data-testid={`force-override-row-${it.id}`}>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                    {it.timestamp ? new Date(it.timestamp).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <div>{it.operator || "—"}</div>
                    {it.role && (
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{it.role}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {it.master_company_name || (
                      <span className="text-slate-400">—</span>
                    )}
                    {it.master_company_id && (
                      <div className="text-[10px] font-mono text-slate-500">{it.master_company_id.slice(0, 8)}…</div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {it.origin_country || "—"} → {it.destination_country || "—"}
                  </td>
                  <td className="px-3 py-2">
                    {it.reason && it.reason !== "not_provided" ? (
                      it.reason
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700 text-[11px] uppercase tracking-wider font-semibold">
                        Missing
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {it.lane_present ? (
                      it.lane_restricted ? (
                        <span className="text-[11px] px-1.5 py-0.5 rounded border bg-red-50 text-red-700 border-red-200">
                          Restricted
                        </span>
                      ) : (
                        <span className="text-[11px] px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                          Configured
                        </span>
                      )
                    ) : (
                      <span className="text-[11px] px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">
                        No lane
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
